import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LOGO_EMBLEM } from "@/lib/brand";
import ContentManagement from "@/components/admin/ContentManagement";
import ImageField from "@/components/admin/ImageField";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  adminCreateEvent,
  adminCreateFighter,
  adminDeleteEvent,
  adminDeleteFighter,
  adminGetMessages,
  adminGetSubscribers,
  adminUpdateEvent,
  adminUpdateEventEnquiry,
  adminUpdateFighter,
  formatError,
  getEvents,
  getFighters,
} from "@/lib/api";

const inputCls = "field-underline w-full font-dm text-[#FAFAFA] py-2.5 text-sm placeholder:text-[#A3A3A3]/50";
const labelCls = "font-oswald uppercase tracking-[0.2em] text-[11px] text-[#A3A3A3] block mb-1";
const goldBtn = "font-oswald uppercase tracking-[0.15em] text-xs bg-[#E85A24] text-black px-5 py-2.5 hover:bg-[#FF7038] transition-colors duration-300 disabled:opacity-60 inline-flex items-center gap-2";
const ghostBtn = "font-oswald uppercase tracking-[0.15em] text-xs border border-white/20 text-[#A3A3A3] px-4 py-2 hover:border-[#E85A24] hover:text-[#E85A24] transition-colors duration-300 inline-flex items-center gap-2";

const EVENT_FIELDS = [
  ["title", "Title", "text"],
  ["date", "Date (YYYY-MM-DD)", "date"],
  ["venue", "Venue", "text"],
  ["city", "City", "text"],
  ["main_event", "Main Event", "text"],
  ["status", "Status", "select", ["upcoming", "past"]],
  ["enquiry", "Enquiry", "select", ["", "Enquiry"]],
  ["ticket_url", "Ticket URL", "url"],
  ["image", "Event image", "image"],
];

const FIGHTER_FIELDS = [
  ["name", "Name", "text"],
  ["nickname", "Nickname", "text"],
  ["weight_class", "Weight Class", "text"],
  ["record", "Record (e.g. 12-0-0)", "text"],
  ["nationality", "Nationality", "text"],
  ["stance", "Stance", "text"],
  ["knockouts", "Knockouts", "number"],
  ["image", "Fighter image", "image"],
];

const emptyEvent = {
  title: "",
  date: "",
  venue: "",
  city: "",
  main_event: "",
  status: "upcoming",
  enquiry: "",
  ticket_url: "",
  image: "",
};

const emptyFighter = {
  name: "",
  nickname: "",
  weight_class: "",
  record: "",
  nationality: "",
  stance: "Orthodox",
  knockouts: 0,
  image: "",
};

function EntityDialog({ title, fields, initial, onSave, trigger, imageFolder, authHeader }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      setOpen(false);
    } catch (error) {
      toast.error(formatError(error, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-[#FAFAFA] rounded-none max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-anton uppercase text-2xl text-[#FAFAFA]">{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          {fields.map(([key, label, type, options]) => {
            if (type === "image") {
              return (
                <ImageField
                  key={key}
                  label={label}
                  value={form[key]}
                  onChange={(value) => setForm((current) => ({ ...current, [key]: value }))}
                  folder={imageFolder}
                  authHeader={authHeader}
                  required
                />
              );
            }

            return (
              <div key={key} className={key === "title" || key === "name" || key === "ticket_url" ? "sm:col-span-2" : ""}>
                <label className={labelCls}>{label}</label>
                {type === "select" ? (
                  <select
                    value={form[key] ?? ""}
                    onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
                    className={`${inputCls} bg-[#0a0a0a]`}
                  >
                    {options.map((option) => (
                      <option key={option || "not-selected"} value={option}>
                        {option || "Not selected"}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    data-testid={`field-${key}`}
                    type={type}
                    required={key !== "ticket_url"}
                    placeholder={key === "ticket_url" ? "https://tickets.example.com/event" : undefined}
                    value={form[key] || ""}
                    onChange={(event) => setForm((current) => ({
                      ...current,
                      [key]: type === "number" ? Number(event.target.value) : event.target.value,
                    }))}
                    className={inputCls}
                  />
                )}
              </div>
            );
          })}
          <DialogFooter className="sm:col-span-2 mt-4">
            <button data-testid="entity-save" type="submit" disabled={saving} className={goldBtn}>
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteButton({ onConfirm, label }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid="delete-trigger" className="text-[#A3A3A3] hover:text-red-400 transition-colors p-2">
          <Trash2 size={16} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#0a0a0a] border border-white/10 text-[#FAFAFA] rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-anton uppercase">Delete {label}?</AlertDialogTitle>
          <AlertDialogDescription className="font-dm text-[#A3A3A3]">This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-white/20 bg-transparent text-[#A3A3A3] hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="delete-confirm" onClick={onConfirm} className="rounded-none bg-red-500/90 text-white hover:bg-red-500">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function AdminDashboard() {
  const { user, logout, authHeader } = useAuth();
  const navigate = useNavigate();
  const h = authHeader();

  const [events, setEvents] = useState([]);
  const [fighters, setFighters] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [messages, setMessages] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const [eventRows, fighterRows, subscriberRows, messageRows] = await Promise.all([
        getEvents(),
        getFighters(),
        adminGetSubscribers(h),
        adminGetMessages(h),
      ]);
      setEvents(eventRows);
      setFighters(fighterRows);
      setSubscribers(subscriberRows);
      setMessages(messageRows);
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const doLogout = () => {
    logout();
    navigate("/admin");
  };

  const saveEvent = (existing) => async (form) => {
    const isEnquiry = form.enquiry === "Enquiry" || form.enquiry === true || form.enquiry === 1;
    const { enquiry, ...eventFields } = form;
    const payload = { ...eventFields, ticket_url: eventFields.ticket_url || null };

    let savedEvent;
    if (existing) {
      savedEvent = await adminUpdateEvent(existing.id, payload, h);
      await adminUpdateEventEnquiry(existing.id, isEnquiry, h);
      toast.success("Event updated.");
    } else {
      savedEvent = await adminCreateEvent(payload, h);
      await adminUpdateEventEnquiry(savedEvent.id, isEnquiry, h);
      toast.success("Event created.");
    }

    await refresh();
    return savedEvent;
  };

  const saveFighter = (existing) => async (form) => {
    if (existing) {
      await adminUpdateFighter(existing.id, form, h);
      toast.success("Fighter updated.");
    } else {
      await adminCreateFighter(form, h);
      toast.success("Fighter added.");
    }
    await refresh();
  };

  const removeEvent = async (id) => {
    try {
      await adminDeleteEvent(id, h);
      toast.success("Event deleted.");
      await refresh();
    } catch (error) {
      toast.error(formatError(error, "Could not delete event."));
    }
  };

  const removeFighter = async (id) => {
    try {
      await adminDeleteFighter(id, h);
      toast.success("Fighter removed.");
      await refresh();
    } catch (error) {
      toast.error(formatError(error, "Could not delete fighter."));
    }
  };

  const tabs = [
    { value: "events", label: "Events", count: events.length },
    { value: "fighters", label: "Fighters", count: fighters.length },
    { value: "content", label: "Content" },
    { value: "subscribers", label: "Subscribers", count: subscribers.length },
    { value: "messages", label: "Messages", count: messages.length },
  ];

  return (
    <div data-testid="admin-dashboard" className="grain min-h-screen bg-[#050505] text-[#FAFAFA]">
      <header className="border-b border-white/10 sticky top-0 z-40 bg-black/80 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_EMBLEM} alt="Susegado Strike" className="h-11 w-auto" />
            <span className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#A3A3A3] hidden sm:inline">/ Control Room</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className={ghostBtn}><ExternalLink size={14} /> View Site</a>
            <button data-testid="admin-logout" onClick={doLogout} className={ghostBtn}><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <p className="font-oswald uppercase tracking-[0.3em] text-xs text-[#E85A24] mb-2">Welcome, {user?.name || "Admin"}</p>
        <h1 className="font-anton uppercase text-4xl md:text-5xl mb-10">Dashboard</h1>

        <Tabs defaultValue="events" className="w-full">
          <TabsList className="bg-[#0a0a0a] border border-white/10 rounded-none p-0 h-auto flex-wrap">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                data-testid={`tab-${tab.value}`}
                className="rounded-none font-oswald uppercase tracking-[0.15em] text-xs data-[state=active]:bg-[#E85A24] data-[state=active]:text-black text-[#A3A3A3] px-6 py-3"
              >
                {tab.label}{typeof tab.count === "number" ? ` (${tab.count})` : ""}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="events" className="mt-8">
            <div className="flex justify-end mb-4">
              <EntityDialog
                title="New Event"
                fields={EVENT_FIELDS}
                initial={emptyEvent}
                onSave={saveEvent(null)}
                imageFolder="events"
                authHeader={h}
                trigger={<button data-testid="add-event" className={goldBtn}><Plus size={14} /> Add Event</button>}
              />
            </div>
            <div className="border border-white/10 divide-y divide-white/10">
              {events.map((event) => (
                <div key={event.id} data-testid={`admin-event-${event.id}`} className="flex items-center justify-between gap-4 p-4 hover:bg-white/[0.02]">
                  <div className="flex items-center gap-4 min-w-0">
                    {event.image && <img src={event.image} alt="" className="w-14 h-12 object-cover grayscale shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-oswald uppercase text-[#FAFAFA] truncate">{event.title}</p>
                      <p className="font-dm text-xs text-[#A3A3A3]">
                        {event.date} · {event.venue}, {event.city} · {event.main_event}{event.enquiry ? " · Enquiry" : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <EntityDialog
                      title="Edit Event"
                      fields={EVENT_FIELDS}
                      initial={{ ...event, enquiry: event.enquiry ? "Enquiry" : "" }}
                      onSave={saveEvent(event)}
                      imageFolder="events"
                      authHeader={h}
                      trigger={<button data-testid={`edit-event-${event.id}`} className="text-[#A3A3A3] hover:text-[#E85A24] p-2"><Pencil size={16} /></button>}
                    />
                    <DeleteButton label="event" onConfirm={() => removeEvent(event.id)} />
                  </div>
                </div>
              ))}
              {events.length === 0 && <p className="p-6 font-dm text-sm text-[#A3A3A3]">No events yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="fighters" className="mt-8">
            <div className="flex justify-end mb-4">
              <EntityDialog title="New Fighter" fields={FIGHTER_FIELDS} initial={emptyFighter} onSave={saveFighter(null)} imageFolder="fighters" authHeader={h} trigger={<button data-testid="add-fighter" className={goldBtn}><Plus size={14} /> Add Fighter</button>} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {fighters.map((fighter) => (
                <div key={fighter.id} data-testid={`admin-fighter-${fighter.id}`} className="flex items-center justify-between gap-4 p-4 bg-[#050505]">
                  <div className="flex items-center gap-4 min-w-0">
                    <img src={fighter.image} alt="" className="w-12 h-12 object-cover grayscale shrink-0" />
                    <div className="min-w-0">
                      <p className="font-oswald uppercase text-[#FAFAFA] truncate">{fighter.name}</p>
                      <p className="font-dm text-xs text-[#A3A3A3]">{fighter.weight_class} · {fighter.record} · {fighter.knockouts} KO</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <EntityDialog title="Edit Fighter" fields={FIGHTER_FIELDS} initial={fighter} onSave={saveFighter(fighter)} imageFolder="fighters" authHeader={h} trigger={<button data-testid={`edit-fighter-${fighter.id}`} className="text-[#A3A3A3] hover:text-[#E85A24] p-2"><Pencil size={16} /></button>} />
                    <DeleteButton label="fighter" onConfirm={() => removeFighter(fighter.id)} />
                  </div>
                </div>
              ))}
              {fighters.length === 0 && <p className="p-6 font-dm text-sm text-[#A3A3A3]">No fighters yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="content" className="mt-8">
            <ContentManagement authHeader={h} />
          </TabsContent>

          <TabsContent value="subscribers" className="mt-8">
            <div className="border border-white/10 divide-y divide-white/10">
              {subscribers.map((subscriber) => (
                <div key={subscriber.id} className="flex items-center justify-between p-4">
                  <span className="font-dm text-[#FAFAFA]">{subscriber.email}</span>
                  <span className="font-dm text-xs text-[#A3A3A3]">{new Date(subscriber.created_at).toLocaleDateString()}</span>
                </div>
              ))}
              {subscribers.length === 0 && <p className="p-6 font-dm text-sm text-[#A3A3A3]">No subscribers yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-8">
            <div className="space-y-px bg-white/10 border border-white/10">
              {messages.map((message) => (
                <div key={message.id} className="bg-[#050505] p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span className="font-oswald uppercase text-[#FAFAFA]">{message.name}</span>
                    <span className="font-oswald uppercase tracking-widest text-[10px] text-[#E85A24] border border-[#E85A24]/40 px-2 py-0.5">{message.interest}</span>
                  </div>
                  <p className="font-dm text-sm text-[#A3A3A3] mb-2">{message.message}</p>
                  <p className="font-dm text-xs text-[#A3A3A3]/60">{message.email} · {new Date(message.created_at).toLocaleString()}</p>
                </div>
              ))}
              {messages.length === 0 && <p className="p-6 font-dm text-sm text-[#A3A3A3]">No messages yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
